import React, { useState } from 'react';
import { Card, CardHeader, CardContent, CardFooter } from './ui/Card';
import { Button } from './ui/Button';
import { Wifi, Monitor, Home, Zap, Clock, BadgeCheck, Shield, Wrench, Settings, Package, Search, UserCheck, Percent, MapPin, Briefcase, Globe } from 'lucide-react';

interface DepannageOption {
  name: string;
  price: string;
  priceSubtext?: string;
  duration?: string;
  description?: string;
  badge?: string;
  highlight?: boolean;
  icon: React.ReactNode;
  features: string[];
}

const Pricing: React.FC = () => {
  const [depannageType, setDepannageType] = useState<'distance' | 'domicile'>('distance');

  const whatsappNumber = "33695358625";
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;

  const depannages: Record<'distance' | 'domicile', DepannageOption> = {
    distance: {
      name: "Dépannage à distance",
      price: "50",
      priceSubtext: "intervention simple",
      duration: "",
      highlight: true,
      badge: "Populaire",
      icon: <Wifi className="h-5 w-5 text-[#5865F2]" />,
      features: [
        "Disponible partout en France",
        "Diagnostic gratuit inclus",
        "Un expert dédié à votre écoute",
        "Intervention simple (30 min) : 50€",
        "Intervention complexe (60 min) : 80€",
        "Ne payez que si problème est résolu"
      ]
    },
    domicile: {
      name: "Intervention à domicile",
      price: "150",
      priceSubtext: "déplacement inclus",
      description: "1h max incluse",
      icon: <Home className="h-5 w-5 text-[#5865F2]" />,
      features: [
        "Zone : 50 km autour de Genève — Ain (01) · Haute-Savoie (74)",
        "Diagnostic gratuit inclus",
        "Un expert dédié à votre écoute",
        "Jusqu'à 1h d'intervention (au delà sur devis)",
        "Frais de déplacement inclus",
        "Possibilité d'intervention le jour-même",
        "50% de crédit d'impôt (services à domicile)"
      ]
    }
  };

  const selectedDepannage = depannages[depannageType];

  return (
    <section id="pricing" className="section-spacing bg-gradient-to-b from-[#2F3136] to-[#36393F]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center section-title-spacing">
          <div className="section-subtitle-badge">
            <BadgeCheck size={16} className="mr-1.5" />
            <span className="text-sm font-medium">Tarifs transparents</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4 flex items-center justify-center">
            <Package className="mr-2 text-[#5865F2]" size={32} /> Nos offres de&nbsp;<span className="text-[#5865F2]">services</span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto">
            Des solutions adaptées à vos besoins avec diagnostic gratuit et sans engagement
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Dépannages section */}
          <div className="flex justify-center">
            <Card className="h-full relative w-full max-w-md border-2 border-[#5865F2] flex flex-col">
              {selectedDepannage.badge && (
                <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                  <div className="bg-[#5865F2] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                    {selectedDepannage.badge}
                  </div>
                </div>
              )}

              <CardHeader className="bg-[#5865F2]/10 pt-6 pb-6 min-h-[300px]">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center mb-3">
                    <div className="bg-[#5865F2]/20 rounded-full p-2 mr-2">
                      <Wrench className="h-6 w-6 text-[#5865F2]" />
                    </div>
                    <h3 className="text-xl font-bold text-white">Dépannages</h3>
                  </div>

                  <p className="text-gray-300 text-sm mb-4">Solutions ponctuelles pour résoudre vos problèmes</p>

                  <div className="inline-flex flex-wrap items-center justify-center bg-[#2F3136] rounded-lg p-1 mb-3">
                    <button
                      onClick={() => setDepannageType('distance')}
                      className={`flex items-center px-3.5 py-1.5 text-sm rounded-md transition-all duration-300 ${depannageType === 'distance' ? 'bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                    >
                      <Wifi className="h-4 w-4 mr-1.5" />
                      <span>À distance</span>
                    </button>
                    <button
                      onClick={() => setDepannageType('domicile')}
                      className={`flex items-center px-3.5 py-1.5 text-sm rounded-md transition-all duration-300 ${depannageType === 'domicile' ? 'bg-gradient-to-r from-[#5865F2] via-[#4752C4] to-[#5865F2] text-white shadow-lg' : 'text-gray-400 hover:text-white hover:bg-white/5'}`}
                    >
                      <Home className="h-4 w-4 mr-1.5" />
                      <span>À domicile</span>
                    </button>
                  </div>

                  <p className="text-xs text-gray-400 mb-3 flex items-center justify-center gap-1.5">
                    {depannageType === 'distance'
                      ? <><Globe className="h-3.5 w-3.5 text-[#5865F2]" /> Partout en France</>
                      : <><MapPin className="h-3.5 w-3.5 text-[#5865F2]" /> 50 km autour de Genève · Ain (01) · Haute-Savoie (74)</>}
                  </p>

                  <div className="flex flex-col items-center w-full">
                    <div className="flex flex-col items-center">
                      {'priceSubtext' in selectedDepannage && selectedDepannage.priceSubtext && (
                        <span className="text-green-400 text-xs font-medium uppercase tracking-wider mb-1">
                          {selectedDepannage.priceSubtext}
                        </span>
                      )}

                      <div className="flex justify-center items-center mb-1">
                        {depannageType === 'domicile' ? (
                          <div className="flex items-center">
                            <div className="bg-gray-800/50 rounded p-1.5 border border-gray-700">
                              <span className="text-gray-400 text-sm line-through">{selectedDepannage.price} €</span>
                            </div>
                            <div className="bg-green-400/10 rounded p-2 border border-green-400/30 ml-3">
                              <div className="flex flex-col items-center">
                                <span className="text-green-400 text-2xl font-bold">
                                  {(parseFloat(selectedDepannage.price) / 2).toFixed(0)} €
                                </span>
                                <span className="text-xs text-gray-300">après crédit d'impôt</span>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="bg-[#5865F2]/10 rounded p-2 border border-[#5865F2]/30">
                            <span className="text-white text-2xl font-bold">
                              {selectedDepannage.price} €
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1">
                <ul className="space-y-3">
                  {selectedDepannage.features?.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                        {featureIndex === 0 ? <MapPin className="text-green-400 h-3 w-3 flex-shrink-0" /> :
                         featureIndex === 1 ? <Search className="text-green-400 h-3 w-3 flex-shrink-0" /> :
                         featureIndex === 2 ? <UserCheck className="text-green-400 h-3 w-3 flex-shrink-0" /> :
                         featureIndex === 3 ? <Clock className="text-green-400 h-3 w-3 flex-shrink-0" /> :
                         featureIndex === 4 ? <Clock className="text-green-400 h-3 w-3 flex-shrink-0" /> :
                         featureIndex === 5 ? <Shield className="text-green-400 h-3 w-3 flex-shrink-0" /> :
                         <Percent className="text-green-400 h-3 w-3 flex-shrink-0" />}
                      </div>
                      <span className="text-gray-200 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>

              <CardFooter className="mt-auto">
                <Button
                  variant="primary"
                  className="w-full group"
                  onClick={() =>
                    window.open(
                      `${whatsappUrl}?text=${encodeURIComponent(
                        depannageType === 'distance'
                          ? "Bonjour, je souhaite lancer un depannage a distance."
                          : "Bonjour, je souhaite planifier une intervention a domicile."
                      )}`,
                      '_blank'
                    )
                  }
                >
                  {depannageType === 'distance' ? 'Lancer mon dépannage à distance' : 'Planifier mon intervention à domicile'}
                  <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                </Button>
              </CardFooter>
            </Card>
          </div>

          {/* TPE/PME section */}
          <div className="flex justify-center">
            <Card className="h-full relative w-full max-w-md border-2 border-[#5865F2] flex flex-col">
              <div className="absolute top-0 right-0 transform translate-x-2 -translate-y-2">
                <div className="bg-[#5865F2] text-white px-3 py-1 rounded-full text-xs font-semibold shadow-lg">
                  Sur devis
                </div>
              </div>

              <CardHeader className="bg-[#5865F2]/10 pt-6 pb-6 min-h-[300px]">
                <div className="flex flex-col items-center text-center">
                  <div className="flex items-center mb-3">
                    <div className="bg-[#5865F2]/20 rounded-full p-2 mr-2">
                      <Briefcase className="h-6 w-6 text-[#5865F2]" />
                    </div>
                    <h3 className="text-xl font-bold text-white">TPE/PME</h3>
                  </div>

                  <p className="text-gray-300 text-sm mb-4">Support informatique adapté aux besoins des TPE/PME</p>

                  <div className="inline-flex flex-wrap items-center justify-center bg-[#2F3136] rounded-lg p-1 mb-3">
                    <span className="flex items-center px-3.5 py-1.5 text-sm rounded-md text-white bg-[#5865F2]/80 shadow-lg">
                      <Wifi className="h-4 w-4 mr-1.5" />
                      <span>À distance</span>
                    </span>
                    <span className="flex items-center px-3.5 py-1.5 text-sm rounded-md text-white bg-[#5865F2] shadow-lg ml-1">
                      <Home className="h-4 w-4 mr-1.5" />
                      <span>Dans vos locaux</span>
                    </span>
                  </div>

                  <p className="text-xs text-gray-400 mb-3 flex items-center justify-center gap-1.5">
                    <MapPin className="h-3.5 w-3.5 text-[#5865F2]" /> 50 km autour de Genève · Ain (01) · Haute-Savoie (74)
                  </p>

                  <div className="flex flex-col items-center w-full mt-4">
                    <span className="text-green-400 text-xs font-medium uppercase tracking-wider mb-1">
                      devis gratuit & sans engagement
                    </span>
                    <div className="bg-[#5865F2]/10 rounded p-2 border border-[#5865F2]/30">
                      <span className="text-white text-2xl font-bold">
                        Sur mesure
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="flex-1">
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                      <Wifi className="text-green-400 h-3 w-3 flex-shrink-0" />
                    </div>
                    <span className="text-gray-200 text-sm">Support à distance ponctuel ou récurrent</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                      <Home className="text-green-400 h-3 w-3 flex-shrink-0" />
                    </div>
                    <span className="text-gray-200 text-sm">Interventions sur site dans vos locaux</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                      <Monitor className="text-green-400 h-3 w-3 flex-shrink-0" />
                    </div>
                    <span className="text-gray-200 text-sm">Configuration postes, réseau, imprimantes</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                      <Settings className="text-green-400 h-3 w-3 flex-shrink-0" />
                    </div>
                    <span className="text-gray-200 text-sm">Cloud : Microsoft 365, Google Workspace</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                      <Shield className="text-green-400 h-3 w-3 flex-shrink-0" />
                    </div>
                    <span className="text-gray-200 text-sm">Sécurité, sauvegardes et maintenance préventive</span>
                  </li>
                  <li className="flex items-start">
                    <div className="bg-green-400/10 rounded-full p-1 mr-2 mt-0.5">
                      <Zap className="text-green-400 h-3 w-3 flex-shrink-0" />
                    </div>
                    <span className="text-gray-200 text-sm">Réponse rapide et interlocuteur dédié</span>
                  </li>
                </ul>
              </CardContent>

              <CardFooter className="mt-auto">
                <Button
                  variant="primary"
                  className="w-full group"
                  onClick={() =>
                    window.open(
                      `${whatsappUrl}?text=${encodeURIComponent(
                        "Bonjour, je souhaite un audit express pour mon entreprise."
                      )}`,
                      '_blank'
                    )
                  }
                >
                  Demander mon audit express
                  <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1">→</span>
                </Button>
              </CardFooter>
            </Card>
          </div>
        </div>

        <div className="mt-16 text-center bg-[#2F3136] p-8 rounded-xl border border-gray-700 shadow-xl">
          <div className="flex items-center justify-center mb-4">
            <Shield className="text-[#5865F2] h-8 w-8 mr-3" />
            <h3 className="text-2xl font-bold text-white">Besoin d'un service <span className="text-[#5865F2]">personnalisé ?</span></h3>
          </div>
          <p className="text-gray-300 mb-4 max-w-2xl mx-auto">
            Nous proposons des solutions sur mesure adaptées à vos besoins spécifiques. Contactez-nous pour un devis gratuit.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto mb-6">
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Clock className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Intervention +60min</span>
            </div>
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Home className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Domicile non couvert par un expert</span>
            </div>
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Settings className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Configuration pour les TPE/PME</span>
            </div>
            <div className="bg-[#36393F] p-4 rounded-lg border border-gray-700 flex flex-col items-center text-center h-32">
              <div className="bg-[#5865F2]/20 rounded-full p-3 mb-2">
                <Monitor className="text-[#5865F2] h-5 w-5" />
              </div>
              <span className="text-white text-sm font-medium">Matériel spécifique</span>
            </div>
          </div>
          <Button
            variant="primary"
            size="lg"
            className="px-8"
            onClick={() =>
              window.open(
                `${whatsappUrl}?text=${encodeURIComponent(
                  "Bonjour, j'ai un besoin specifique et je souhaite un devis personnalise."
                )}`,
                '_blank'
              )
            }
          >
            Demander un devis personnalisé
          </Button>
        </div>
      </div>
    </section>
  );
};

export default Pricing;
